import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import {
    checkCustomerRewardEligibility,
    scanCustomerPassEmail,
    scanQRCodeCustomerPass,
    updateCustomerPoints,
} from '../services/scanApi';
const logger = createLogger('scanSlice');

export const initialState = {
    loading: false,
    rewardsLoading: false,
    /** Latest getAvailableRewards requestId; ignore fulfilled/rejected from older requests (race-safe). */
    latestRewardsRequestId: null,
    /** Bumped on each successful email/QR customer load so rewards refetch when passId is unchanged (repeat search). */
    customerLookupGeneration: 0,
    error: null,
    mode: null,
    message: null,
    email: null,
    customerData: {},
    passId: null,
    availableRewards: null,
    pointRatio: null,
    shouldFocusInput: false,
    selectedPassIndex: 0,
};

//returns the customer data or error
export const emailSearch = createAsyncThunk('email/search', async (email, { rejectWithValue }) => {
    try {
        const res = await scanCustomerPassEmail(email);
        return res.data;
    } catch (error) {
        logger.error('Email search failed', error);
        const errorMessage = extractErrorMessage(error);
        return rejectWithValue({ error: errorMessage });
    }
});

//returns the customer data or error
export const qrSearch = createAsyncThunk('qr/search', async (passId, { rejectWithValue }) => {
    try {
        const res = await scanQRCodeCustomerPass(passId);
        return res.data;
    } catch (error) {
        logger.error('QR search failed', error);
        const errorMessage = extractErrorMessage(error);
        return rejectWithValue({ error: errorMessage });
    }
});

//const rewardsRes = await checkCustomerRewardEligibility(res.data.passId);
export const getAvailableRewards = createAsyncThunk(
    'rewards/getAvailable',
    async (passId, { rejectWithValue }) => {
        try {
            logger.info('Getting available rewards for pass:', passId);
            const res = await checkCustomerRewardEligibility(passId);
            return res.data;
        } catch (error) {
            logger.error('Error getting available Reward', error);
            const errorMessage = extractErrorMessage(error);
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// Async thunk for updating customer points
export const updateCustomerPointsThunk = createAsyncThunk(
    'scan/updateCustomerPoints',
    async ({ passId, newPoints, addVisit, moneySpent }, { rejectWithValue, dispatch }) => {
        try {
            const res = await updateCustomerPoints(passId, newPoints, addVisit, moneySpent);
            dispatch(getAvailableRewards(passId));
            return res.data; // should contain updated pass info
        } catch (error) {
            logger.error('Error updating customer points', error);
            const errorMessage = extractErrorMessage(error);
            return rejectWithValue({ error: errorMessage });
        }
    }
);

const scanSlice = createSlice({
    name: 'scan',
    initialState,
    reducers: {
        setMode: (state, action) => {
            state.mode = action.payload;
        },
        setEmail: (state, action) => {
            logger.info('Set email process started in useScan hook', action.payload);
            state.email = action.payload;
        },
        setSelectedPassIndex: (state, action) => {
            state.selectedPassIndex = action.payload;
        },
        // Reducer to update a pass in customerData.allPasses
        updatePassInCustomerData: (state, action) => {
            const updatedPass = action.payload;
            if (state.customerData && Array.isArray(state.customerData.allPasses)) {
                state.customerData.allPasses = state.customerData.allPasses.map((pass) =>
                    pass.passId === updatedPass.passId ? { ...pass, ...updatedPass } : pass
                );
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(emailSearch.pending, (state) => {
                state.loading = true;
                state.rewardsLoading = false;
                state.latestRewardsRequestId = null;
                state.error = null;
                state.message = null;
                state.availableRewards = [];
                state.pointRatio = null;
                state.selectedPassIndex = 0;
            })
            .addCase(emailSearch.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = null;
                state.customerData = action.payload;
                state.selectedPassIndex = 0;
                state.passId = action.payload.passId;
                state.customerLookupGeneration += 1;
            })
            .addCase(emailSearch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.error;
                state.message = null;
                state.customerData = {};
                state.selectedPassIndex = 0;
                state.passId = null;
            })
            .addCase(qrSearch.pending, (state) => {
                state.loading = true;
                state.rewardsLoading = false;
                state.latestRewardsRequestId = null;
                state.error = null;
                state.message = null;
                state.availableRewards = [];
                state.pointRatio = null;
                state.selectedPassIndex = 0;
            })
            .addCase(qrSearch.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.email = action.payload.customerEmail;
                state.message = null;
                state.selectedPassIndex = 0;
                state.passId = action.payload.passId;
                state.customerData = {
                    ...action.payload,
                    allPasses: [action.payload],
                };
                state.customerLookupGeneration += 1;
            })
            .addCase(qrSearch.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.error;
                state.message = null;
                state.customerData = {};
                state.selectedPassIndex = 0;
                state.passId = null;
            })
            // getAvailableRewards uses rewardsLoading only (no top message)
            .addCase(getAvailableRewards.pending, (state, action) => {
                state.rewardsLoading = true;
                state.latestRewardsRequestId = action.meta.requestId;
            })
            .addCase(getAvailableRewards.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.latestRewardsRequestId) {
                    return;
                }
                state.rewardsLoading = false;
                state.availableRewards = action.payload;
            })
            .addCase(getAvailableRewards.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestRewardsRequestId) {
                    return;
                }
                state.rewardsLoading = false;
                state.availableRewards = null;
            })
            .addCase(updateCustomerPointsThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCustomerPointsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                // Update the pass in customerData.allPasses
                const updatedPass = action.payload;
                if (state.customerData && Array.isArray(state.customerData.allPasses)) {
                    state.customerData.allPasses = state.customerData.allPasses.map((pass) =>
                        pass.passId === updatedPass.passId ? { ...pass, ...updatedPass } : pass
                    );
                }
            })
            .addCase(updateCustomerPointsThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to update customer points';
            });
    },
});

export const { setMode, setEmail, setSelectedPassIndex, updatePassInCustomerData } =
    scanSlice.actions;
export default scanSlice.reducer;

export const scanSliceAPI = {
    emailSearch,
    qrSearch,
    getAvailableRewards,
    updateCustomerPointsThunk,
};
